<script setup lang="ts">
  import SidebarLink from '~/components/UI/Utilities/SidebarLink.vue';
  import { Role } from '~/types/auth';
  import { ChevronRightIcon } from '@heroicons/vue/solid';
  import CloseIcon from '~/assets/Icons/CloseIcon.vue';

  const props = defineProps<{
    open: boolean;
    type: 'dashboard' | 'reports' | null;
  }>();

  const emit = defineEmits(['close-sidebar']);

  const closeSidebar = () => {
    emit('close-sidebar');
  };

  const { isExpenseEnabled, role } = useAuth();
  const userRoles = computed(() => role.value);
  const route = useRoute();
  const activeCategory = ref<string | null>(null);
  const isUtilitiesOnly = computed(() => userRoles.value.length === 1 && userRoles.value[0] === Role.UtilitiesAccess);

  const setActiveCategory = (title: string) => {
    activeCategory.value = activeCategory.value === title ? null : title;
  };

  const hasRole = (allowedRoles: string[]) => {
    if (allowedRoles.includes('all')) {
      return true;
    }
    return userRoles.value.some(userRole => allowedRoles.includes(userRole));
  };

  const analysisLinks = [
    // { to: '/', label: 'Home', roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.UtilitiesAccess] },
    // { to: '/organization-analysis', label: 'Organization Analysis', roles: [Role.Admin] },
    {
      to: '/donor-analysis',
      label: 'Donor Analysis',
      roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.DonorReportManager]
    },
    {
      to: '/donation-analysis',
      label: 'Donation Analysis',
      roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.DonorReportManager]
    },
    {
      to: '/pledges-analysis',
      label: 'Pledges Analysis',
      roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.DonorReportManager]
    },
    {
      to: '/grant-analysis',
      label: 'Grant Analysis',
      roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.GrantReportManager, Role.DonorAuditor]
    },
    {
      to: '/grant-utilization-analysis',
      label: 'Grant Utilization Analysis',
      roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.GrantReportManager],
      requiresExpense: isExpenseEnabled
    },
    {
      to: '/expense-analysis',
      label: 'Expense Analysis',
      roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.ExpenseReportManager],
      requiresExpense: isExpenseEnabled
    },
    // {
    //   to: '/payee-analysis',
    //   label: 'Payee Analysis',
    //   roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.ExpenseReportManager],
    //   requiresExpense: isExpenseEnabled
    // },
    {
      to: '/budget-analysis',
      label: 'Budget Analysis',
      roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.BudgetReportManager],
      requiresExpense: isExpenseEnabled
    }
    // {
    //   to: '/purpose-analysis',
    //   label: 'Purpose Analysis',
    //   roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.ExpenseReportManager]
    // }
    // {
    //   to: '/user-analysis',
    //   label: 'User Analysis',
    //   roles: [Role.Admin]
    // }
  ];

  const reportCategories = [
    {
      title: 'Donation',
      links: [
        {
          to: '/donation-report',
          label: 'Donation Report',
          roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.DonorReportManager]
        }
      ]
    },
    {
      title: 'Donor',
      links: [
        {
          to: '/donor-report',
          label: 'Donor Report',
          roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.DonorReportManager]
        }
      ]
    },
    {
      title: 'Grants',
      links: [
        {
          to: '/user-vs-grants',
          label: 'User vs Grants',
          roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.GrantReportManager]
        },
        {
          to: '/grant-utilization-report',
          label: 'Grant Utilization Report',
          roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.GrantReportManager],
          requiresExpense: isExpenseEnabled
        },
        {
          to: '/fund-balance-report',
          label: 'Fund Balance Report',
          roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.GrantReportManager],
          requiresExpense: isExpenseEnabled
        },
        {
          to: '/all-grants-data-export',
          label: 'All Grants Data Export',
          roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.GrantReportManager, Role.DonorAuditor]
        },
        {
          to: '/grant-monthly-balance-report',
          label: 'Grant Monthly Balance Report',
          roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.GrantReportManager, Role.DonorAuditor],
          requiresExpense: isExpenseEnabled
        }
      ]
    },
    {
      title: 'Budget',
      links: [
        {
          to: '/all-budget-data-export',
          label: 'All Budget Data Export',
          roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.BudgetReportManager],
          requiresExpense: isExpenseEnabled
        }
      ]
    },
    {
      title: 'Payee',
      links: [
        {
          to: '/payee-report',
          label: 'Payee Report',
          roles: [Role.Admin, Role.PayeeAdmin, Role.ReadOnly, Role.ExpenseReportManager],
          requiresExpense: isExpenseEnabled
        }
      ]
    },
    {
      title: 'Expense',
      links: [
        {
          to: '/expenses-report',
          label: 'Expenses Report',
          roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.ExpenseReportManager],
          requiresExpense: isExpenseEnabled
        },
        {
          to: '/payment-report',
          label: 'Payment Report',
          roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.ExpenseReportManager],
          requiresExpense: isExpenseEnabled
        },
        {
          to: '/expense-attachment-report',
          label: 'Expense Attachment Report',
          roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.ExpenseReportManager],
          requiresExpense: isExpenseEnabled
        },
        {
          to: '/payee-expense-report',
          label: 'Payee Expense Report',
          roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.ExpenseReportManager],
          requiresExpense: isExpenseEnabled
        }
      ]
    },
    {
      title: 'Advance',
      links: [
        {
          to: '/advance-payment-report',
          label: 'Advance Payment Report',
          roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.ExpenseReportManager, Role.AdvanceAdmin],
          requiresExpense: isExpenseEnabled
        }
      ]
    }
  ];

  watch(
    () => route.path,
    () => {
      const match = reportCategories.find(category => category.links.some(link => link.to === route.path));
      if (match) activeCategory.value = match.title;
    },
    { immediate: true }
  );
</script>

<template>
  <Transition name="sidebar">
    <div
      v-if="props.open"
      class="flex flex-col w-[264px] border-r border-zinc-200 overflow-y-auto fixed bottom-0 top-[56px] bg-white z-10 left-[60px] shadow-[5px_0_10px_-5px_rgba(0,0,0,0.3)]"
    >
      <!-- DASHBOARD -->
      <template v-if="props.type === 'dashboard'">
        <template v-if="isUtilitiesOnly">
          <!-- <SidebarLink to="/" label="Home" /> -->
        </template>
        <template v-else>
          <div class="border-b px-2 py-3 border-gray-300 bg-zinc-100 flex flex-row justify-between items-center">
            <div class="text-sm font-semibold text-gray-700">Dashboard</div>
            <div @click="closeSidebar">
              <CloseIcon class="w-[18px] h-[18px] text-gray-700 cursor-pointer" />
            </div>
          </div>
          <template v-for="link in analysisLinks" :key="link.to">
            <SidebarLink
              v-if="hasRole(link.roles) && (!link.requiresExpense || isExpenseEnabled)"
              :to="link.to"
              :label="link.label"
              @click="closeSidebar"
            />
          </template>
        </template>
      </template>

      <!-- REPORTS -->
      <template v-if="props.type === 'reports'">
        <!-- <SidebarSection title="Reports"> -->
        <div class="border-b px-2 py-3 border-gray-300 bg-zinc-100 flex flex-row justify-between items-center">
          <div class="text-sm font-semibold text-gray-700">Reports</div>
          <div @click="closeSidebar">
            <CloseIcon class="w-[18px] h-[18px] text-gray-700 cursor-pointer" />
          </div>
        </div>
        <div class="space-y-1 pt-2.5 px-2">
          <div
            v-for="category in reportCategories.filter(cat =>
              cat.links.some(l => !l.requiresExpense || isExpenseEnabled)
            )"
            :key="category.title"
          >
            <div
              class="px-3 py-2 text-sm font-medium cursor-pointer rounded-md flex justify-between items-center text-zinc-700 hover:bg-zinc-100 bg-[#0000000a]"
              @click="setActiveCategory(category.title)"
            >
              {{ category.title }}
              <ChevronRightIcon
                class="w-5 h-5 transition-transform duration-300"
                :class="{
                  'rotate-90 text-zinc-400': activeCategory === category.title,
                  'text-zinc-400': activeCategory !== category.title
                }"
              />
            </div>
            <Transition name="accordion">
              <div
                v-if="activeCategory === category.title"
                class="mt-1 space-y-1 bg-white p-1 overflow-hidden shadow-[2px_2px_6px_rgba(0,0,0,0.12)] mb-2"
              >
                <template
                  v-for="link in category.links.filter(l => !l.requiresExpense || isExpenseEnabled)"
                  :key="link.to"
                >
                  <NuxtLink
                    :to="link.to"
                    class="flex items-center text-sm p-2 rounded-md transition-colors"
                    :class="
                      route.path === link.to
                        ? 'bg-blue-50 border border-blue-200 font-semibold text-blue-800 shadow-[0_2px_2px_0_#00000014]'
                        : 'text-zinc-800 hover:bg-zinc-200'
                    "
                    @click="closeSidebar"
                  >
                    {{ link.label }}
                  </NuxtLink>
                </template>
              </div>
            </Transition>
          </div>
        </div>
        <!-- </SidebarSection> -->
      </template>
    </div>
  </Transition>
</template>

<style scoped>
  .sidebar-enter-active,
  .sidebar-leave-active {
    transition: all 0.5s ease;
  }

  .sidebar-enter-from {
    transform: translateX(-324px);
    opacity: 0;
  }

  .sidebar-leave-to {
    transform: translateX(-324px);
    opacity: 0;
  }
</style>
